import React, { useEffect, useState } from 'react';

import DataTable from '../shared/data_table';
import TpDialog from '../shared/tp_dialog';
import StatusPill from '../shared/status_pill';
import LoadFailed from '../shared/load_failed';
import { Ledger, StatusCell, What, Who } from '../shared/cells';
import { fmt } from '../format';
import { post_base_rate } from '../api';
import {
  NOTE_MAX,
  NOTE_MIN,
  can_save,
  day_label,
  form_errors,
  is_opening,
  note_length,
  preview_text,
  refusal_text,
  request_body,
  saved_text,
  stamp_ist,
  status_view
} from './logic';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { BASE_RATE_CSV_COLUMNS, base_rate_csv_name } from './csv';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import usePayTimeWarning from '../../../util_components/pay_time_warning/use_pay_time_warning';
import {
  DETAIL_EFFECT,
  with_pay_time_ack
} from '../../../util_components/pay_time_warning/pay_time_warning_rules';
import './base_rate.css';

/*
 * the base rate history pop-up: the append-only history plus, for users who may change rates, the
 * add form. The parent owns the history fetch (it also drives the profile's rate line).
 */
const EMPTY_FORM = { rate: '', effective_date: '', note: '' };

// The rate history: a table where it fits, labelled cards where it does not (responsive_table).
const rate_chip = (e) => {
  const st = status_view(e.status);
  return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
};

// Four tracks (list-row pattern): effective date · note and who · rate · status. Cards below 600px.
const HISTORY_COLUMNS = [
  {
    key: 'effective',
    role: 'identifier',
    label: 'From (IST)',
    track: { compact: '130px', full: '140px' },
    render: (e) => <Who name={day_label(e.effective_from_ist)} />
  },
  {
    key: 'note',
    label: 'Note',
    render: (e) => (
      <What
        title={<span className="tp-brh-note">{is_opening(e) ? 'Opening rate' : e.note}</span>}
        lines={[
          <>
            {e.created_by_name || '—'}
            {e.created_by_email ? (
              <span className="tp-brh-email"> · {e.created_by_email}</span>
            ) : null}
          </>,
          stamp_ist(e.created_at_ist)
        ]}
      />
    )
  },
  {
    key: 'rate',
    role: 'amount',
    label: 'Rate',
    align: 'right',
    // Wide enough for a paise rate in the lakhs (₹12,34,567.89) without clipping.
    track: { compact: '148px', full: '152px' },
    render: (e) => <span className="tp-c-amt">{fmt(e.base_rate)}</span>,
    card_render: (e) => <Ledger items={[{ label: 'Rate per 60 min', value: fmt(e.base_rate) }]} />
  },
  {
    key: 'status',
    role: 'status',
    label: 'Status',
    track: { compact: '112px', full: '124px' },
    render: (e) => <StatusCell chip={rate_chip(e)} />
  }
];

const HistoryTable = ({ entries }) => {
  if (!entries.length) return <div className="tp-empty">No rate history yet.</div>;
  return (
    <DataTable columns={HISTORY_COLUMNS} rows={entries} rowKey={(e) => e.uuid} card_below={600} />
  );
};

const AddForm = ({ teacher_uuid, data, busy, locked, set_busy, on_dirty, on_saved, now }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [refusal, setRefusal] = useState('');
  const [saved, setSaved] = useState('');
  const pw = usePayTimeWarning(); // A-52: e.g. a back-dated effective date, acknowledged with a note

  const bounds = {
    earliest_allowed_date: data.earliest_allowed_date,
    latest_allowed_date: data.latest_allowed_date
  };
  const errors = form_errors(form, bounds);
  const valid = can_save(form, bounds);
  const preview = valid ? preview_text(data.entries, form, now) : '';
  const dirty = form.rate !== '' || form.effective_date !== '' || form.note !== '';
  useEffect(() => on_dirty(dirty), [dirty, on_dirty]);

  const set_field = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setRefusal('');
    setSaved('');
  };
  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));
  // An error shows once the field was left, or as soon as it holds something.
  const err = (field) => (touched[field] || form[field] !== '' ? errors[field] : '');

  // ack: the pay-time acknowledgement from the warning step; null on the first send
  const send = (ack = null) => {
    if (!valid || busy || locked) return;
    set_busy(true);
    setRefusal('');
    post_base_rate(with_pay_time_ack(request_body(teacher_uuid, form), ack))
      .then((result) => {
        pw.clear();
        const text = saved_text(result, form);
        setSaved(text);
        setForm(EMPTY_FORM);
        setTouched({});
        on_saved(text);
      })
      .catch((e2) => {
        if (pw.catch_warning(e2)) return;
        pw.clear();
        console.error('base rate save failed', e2?.response?.status || e2?.message);
        setRefusal(refusal_text(e2));
      })
      .finally(() => set_busy(false));
  };

  const submit = (e) => {
    e.preventDefault();
    send();
  };

  const field_id = (f) => `brh-${f}`;
  const described = (f, hint) =>
    [hint ? `${field_id(f)}-hint` : '', err(f) ? `${field_id(f)}-err` : '']
      .filter(Boolean)
      .join(' ') || undefined;

  if (pw.warning) {
    return (
      <div className="tp-form">
        <h4 className="tp-brh-h">Add a rate change</h4>
        <PayTimeWarningStep
          warning={pw.warning}
          detail={DETAIL_EFFECT}
          busy={busy}
          changeLabel="Change date"
          actions="tp"
          onAcknowledge={(ack) => send(ack)}
          onChange={pw.clear}
        />
      </div>
    );
  }

  return (
    <form className="tp-form" onSubmit={submit} aria-label="Add a rate change" noValidate>
      <h4 className="tp-brh-h">Add a rate change</h4>
      <fieldset disabled={busy || locked} style={{ border: 0, padding: 0, margin: 0 }}>
        <div className="tp-field-2">
          <div className="tp-field">
            <label htmlFor={field_id('rate')}>New rate (₹ per 60 min)</label>
            <input
              id={field_id('rate')}
              className="tp-input"
              inputMode="numeric"
              required
              aria-invalid={err('rate') ? 'true' : undefined}
              aria-describedby={described('rate')}
              value={form.rate}
              onChange={(e) => set_field('rate', e.target.value)}
              onBlur={() => touch('rate')}
              placeholder="e.g. 900"
            />
            {err('rate') ? (
              <span id={`${field_id('rate')}-err`} className="tp-err">
                {err('rate')}
              </span>
            ) : null}
          </div>
          <div className="tp-field">
            <label htmlFor={field_id('effective_date')}>Effective from (00:00 IST)</label>
            <input
              id={field_id('effective_date')}
              type="date"
              className="tp-input"
              required
              min={data.earliest_allowed_date || undefined}
              max={data.latest_allowed_date || undefined}
              aria-invalid={err('effective_date') ? 'true' : undefined}
              aria-describedby={described('effective_date', true)}
              value={form.effective_date}
              onChange={(e) => set_field('effective_date', e.target.value)}
              onBlur={() => touch('effective_date')}
            />
            <span id={`${field_id('effective_date')}-hint`} className="tp-hint">
              {day_label(data.earliest_allowed_date)} to {day_label(data.latest_allowed_date)}.
              Closed months stay frozen.
            </span>
            {err('effective_date') ? (
              <span id={`${field_id('effective_date')}-err`} className="tp-err">
                {err('effective_date')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="tp-field">
          <label htmlFor={field_id('note')}>Note (why the rate changes)</label>
          <textarea
            id={field_id('note')}
            className="tp-textarea"
            required
            maxLength={NOTE_MAX}
            aria-invalid={err('note') ? 'true' : undefined}
            aria-describedby={described('note', true)}
            value={form.note}
            onChange={(e) => set_field('note', e.target.value)}
            onBlur={() => touch('note')}
            placeholder="e.g. Annual revision approved by Ops"
          />
          <span id={`${field_id('note')}-hint`} className="tp-hint">
            {note_length(form.note)}/{NOTE_MAX} characters, at least {NOTE_MIN}.
          </span>
          {err('note') ? (
            <span id={`${field_id('note')}-err`} className="tp-err">
              {err('note')}
            </span>
          ) : null}
        </div>
      </fieldset>

      {preview ? (
        <div className="tp-banner tp-banner-info" data-testid="brh-preview">
          {preview}
        </div>
      ) : null}
      {refusal ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {refusal}
        </div>
      ) : null}
      {saved ? (
        <div className="tp-banner tp-banner-ok" role="status">
          {saved}
        </div>
      ) : null}

      <div className="tp-modal-ft">
        <button
          type="button"
          className="tp-btn-out"
          disabled={busy || locked || !dirty}
          onClick={() => {
            setForm(EMPTY_FORM);
            setTouched({});
            setRefusal('');
          }}
        >
          Clear
        </button>
        <button type="submit" className="tp-btn" disabled={!valid || busy || locked}>
          {busy ? 'Saving…' : 'Save rate change'}
        </button>
      </div>
    </form>
  );
};

const BaseRateHistoryDialog = ({
  teacher_uuid,
  teacher_name,
  data,
  loading,
  error,
  on_retry,
  on_saved,
  on_close,
  now
}) => {
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const entries = (data && Array.isArray(data.entries) && data.entries) || [];
  const title = teacher_name ? `Base rate history: ${teacher_name}` : 'Base rate history';
  return (
    <TpDialog
      title={title}
      busy={busy}
      guard_text={
        dirty ? 'You have an unsaved rate change. Save or clear it, or use ✕ to discard it.' : ''
      }
      size="lg"
      on_close={on_close}
    >
      <div className="tp-modal tp-brh">
        {data ? (
          <p className="tp-brh-lead">
            Current rate: <span className="tp-strong">{fmt(data.current_rate)}</span> per 60 min.
            Each class uses the rate in effect at its start time (IST). Entries can't be edited or
            removed; a new entry for the same date replaces the earlier one.
          </p>
        ) : null}
        {error ? <LoadFailed text={error} on_retry={on_retry} /> : null}
        {!data && loading ? (
          <div className="tp-empty" role="status">
            Loading rate history…
          </div>
        ) : null}
        {/* With the add form below, the history scrolls in its own box so Save stays in view. */}
        {data && entries.length ? (
          <div className="tp-brh-csv">
            <DownloadCsvButton
              columns={BASE_RATE_CSV_COLUMNS}
              rows={entries}
              file_name={() => base_rate_csv_name(teacher_name)}
            />
          </div>
        ) : null}
        {data ? (
          <div className={data.can_change ? 'tp-brh-history' : undefined}>
            <HistoryTable entries={entries} />
          </div>
        ) : null}
        {loading && data ? (
          <div className="tp-hint" role="status">
            Refreshing…
          </div>
        ) : null}
        {/* Shown only once a load confirms can_change; locked (read-only) while a reload is in flight. */}
        {data && data.can_change ? (
          <div className="tp-brh-add">
            <AddForm
              teacher_uuid={teacher_uuid}
              data={data}
              busy={busy}
              locked={loading}
              set_busy={setBusy}
              on_dirty={setDirty}
              on_saved={on_saved}
              now={now}
            />
          </div>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default BaseRateHistoryDialog;
